import store from '../redux/Store';
import { fetchNotifications } from '../api/notifications';
import {
  replaceServerNotifications,
  selectNotificationsSyncScope,
} from '../redux/notificationsSlice';

// Pulls the server's notices for the current location into the inbox. Runs
// once per location at a time. A sync is dropped when its page would overwrite
// something newer: the user switched location, or changed the inbox (read,
// delete, clear) while it was in flight. A sync dropped for a change runs
// again once the server writes behind that change have settled.

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 400;

const inFlight = new Map<string, Promise<void>>();
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

// Bumped when a server write starts and when it settles: a sync that fetched
// before the bump carries a list from before the change.
let inboxVersion = 0;
let pendingWrites = 0;
let resyncWhenWritesSettle = false;

function readScope(): { locationId: string } | null {
  const state = store.getState() as any;
  if (!selectNotificationsSyncScope(state)) return null;
  return { locationId: String(state.location.currentLocationId) };
}

function currentLocationId(): string {
  return String((store.getState() as any)?.location?.currentLocationId || '');
}

function dropStaleSync(): void {
  if (pendingWrites > 0) {
    resyncWhenWritesSettle = true;
  } else {
    syncNotificationsSoon();
  }
}

/**
 * Tracks a server write (read, unread, delete, mark all, clear) so that a
 * sync overlapping it is dropped and one fresh sync runs after it settles.
 * Resolves or rejects exactly as the write does, so callers keep their own
 * error handling.
 */
export function trackNotificationWrite<T>(write: Promise<T>): Promise<T> {
  pendingWrites += 1;
  inboxVersion += 1;
  return write.finally(() => {
    pendingWrites -= 1;
    inboxVersion += 1;
    if (pendingWrites === 0 && resyncWhenWritesSettle) {
      resyncWhenWritesSettle = false;
      syncNotificationsSoon();
    }
  });
}

/** @returns whether a sync ran (or joined one) and succeeded */
export async function syncNotifications(): Promise<boolean> {
  const scope = readScope();
  if (!scope) return false;

  const running = inFlight.get(scope.locationId);
  if (running) {
    try {
      await running;
      return true;
    } catch {
      return false;
    }
  }

  const startedAt = inboxVersion;
  const run = (async () => {
    const page = await fetchNotifications({ limit: PAGE_SIZE });
    if (currentLocationId() !== scope.locationId) return;
    if (pendingWrites > 0 || inboxVersion !== startedAt) {
      dropStaleSync();
      return;
    }
    store.dispatch(
      replaceServerNotifications({
        locationId: scope.locationId,
        items: page.docs,
      }),
    );
  })();
  inFlight.set(scope.locationId, run);
  try {
    await run;
    return true;
  } catch (error) {
    console.log('notifications sync failed', error);
    return false;
  } finally {
    inFlight.delete(scope.locationId);
  }
}

/** Coalesces bursts (a push, then the app coming to the foreground) into one sync. */
export function syncNotificationsSoon(delayMs: number = DEBOUNCE_MS): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void syncNotifications();
  }, delayMs);
}
